import { type ReactNode, createContext, useCallback, useContext, useState } from 'react';
import { enableFeatureFlag, getFeatureFlags } from '../../sources/featureFlags';
import { useNotificationDispatch } from '../notifications/NotificationProvider';
import type { FeatureFlagAPI } from '../../types/featureFlags';

type FeatureFlagProviderProps = {
    tenantId: string;
    defaultFlags?: FeatureFlagAPI[];
    children?: ReactNode;
};

type FeatureFlagContext = {
    flags: FeatureFlagAPI[];
    isLoading: boolean;
    getFlag: (flagName: string) => boolean;
    fetchFeatureFlags: () => Promise<void>;
    setFeatureFlag: (id: string, enabled: boolean) => Promise<void>;
};

const FeatureFlagContext = createContext<FeatureFlagContext | null>(null);

export const FeatureFlagProvider = ({
    tenantId,
    defaultFlags,
    children,
}: FeatureFlagProviderProps) => {
    const [flags, setFlags] = useState<FeatureFlagAPI[]>(defaultFlags ?? []);
    const [isLoading, setIsLoading] = useState(false);

    const dispatchNotification = useNotificationDispatch();

    const fetchFeatureFlags = useCallback(async () => {
        try {
            setIsLoading(true);

            const flags = await getFeatureFlags(tenantId);

            setFlags(flags);
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        } finally {
            setIsLoading(false);
        }
    }, [tenantId, dispatchNotification]);

    const setFeatureFlag = useCallback(
        async (id: string, enabled: boolean) => {
            try {
                setIsLoading(true);

                await enableFeatureFlag({ featureFlagId: id, tenantId, enabled });

                await fetchFeatureFlags();
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            } finally {
                setIsLoading(false);
            }
        },
        [tenantId, fetchFeatureFlags, dispatchNotification],
    );

    const getFlag = useCallback(
        (flagName: string) => {
            return flags.find((flag) => flag.developerName === flagName)?.enabled ?? false;
        },
        [flags],
    );

    const contextValue: FeatureFlagContext = {
        flags,
        isLoading,
        getFlag,
        fetchFeatureFlags,
        setFeatureFlag,
    };

    return <FeatureFlagContext value={contextValue}>{children}</FeatureFlagContext>;
};

export const useFeatureFlag = () => {
    const context = useContext(FeatureFlagContext);
    if (!context) {
        throw new Error('useFeatureFlag must be used within a FeatureFlag context');
    }

    return context;
};
